/**
 * Clients List Page
 * 
 * Displays all active clients with search and filters.
 * Links to Client 360° Dashboard for each client.
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Search, Plus, Filter, Users, UserCheck, Clock, AlertCircle,
  ChevronRight, MapPin, Phone, Calendar, Wand2
} from "lucide-react";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useAuth } from "../../contexts/AuthContext";
import { UniversalIntakeForm } from "../../components/ui/universal-intake-form";
import { toast } from "sonner";
import api from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

// Status badge styles
const STATUS_STYLES = {
  active: "bg-green-100 text-green-800 border-green-200",
  pending_activation: "bg-amber-100 text-amber-800 border-amber-200",
  activation_review: "bg-blue-100 text-blue-800 border-blue-200",
  on_hold: "bg-orange-100 text-orange-800 border-orange-200",
  discharged: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABELS = {
  active: "Active",
  pending_activation: "Pending Activation",
  activation_review: "Under Review",
  on_hold: "On Hold",
  discharged: "Discharged",
};

export default function ClientsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAIIntake, setShowAIIntake] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    onHold: 0,
  });

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter === "active") {
        params.append("is_active", "true");
      } else if (statusFilter === "inactive") {
        params.append("is_active", "false");
      }
      
      const res = await fetch(`${API}/api/clients?${params.toString()}`, {
        credentials: "include",
      });
      
      if (res.ok) {
        const data = await res.json();
        setClients(data);
        
        // Calculate stats
        const active = data.filter(c => c.status === "active").length;
        const pending = data.filter(c => ["pending_activation", "activation_review"].includes(c.status)).length;
        const onHold = data.filter(c => c.status === "on_hold").length;
        
        setStats({
          total: data.length,
          active,
          pending,
          onHold,
        });
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search
  const filteredClients = clients.filter(client => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const fullName = `${client.first_name || ""} ${client.last_name || ""}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      client.city?.toLowerCase().includes(searchLower) ||
      client.zip_code?.includes(search)
    );
  });

  return (
    <AppShell>
      <div className="space-y-6" data-testid="clients-list">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
            <p className="text-muted-foreground">
              Manage client profiles, schedules, and care plans
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAIIntake(true)}
              data-testid="ai-intake-client-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              AI Quick Entry
            </Button>
            <Link to="/assessments">
              <Button data-testid="new-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Client Assessment
              </Button>
            </Link>
          </div>
        </div>

        {/* AI Intake Modal for Quick Client Entry */}
        {showAIIntake && (
          <UniversalIntakeForm
            entityType="client"
            isModal={true}
            onClose={() => setShowAIIntake(false)}
            onDataExtracted={async (extractedData) => {
              try {
                // Create client with extracted data
                const response = await api.post('/clients', {
                  first_name: extractedData.first_name,
                  last_name: extractedData.last_name,
                  date_of_birth: extractedData.date_of_birth,
                  gender: extractedData.gender,
                  phone: extractedData.phone,
                  email: extractedData.email,
                  address: extractedData.address,
                  city: extractedData.city,
                  state: extractedData.state,
                  zip_code: extractedData.zip_code,
                  primary_diagnosis: extractedData.primary_diagnosis,
                  secondary_diagnosis: extractedData.secondary_diagnosis,
                  emergency_contact_name: extractedData.emergency_contact_name,
                  emergency_contact_phone: extractedData.emergency_contact_phone,
                  emergency_contact_relationship: extractedData.emergency_contact_relationship,
                  funding_source: extractedData.funding_source,
                  status: 'pending_activation',
                });
                
                if (response?.data?.id) {
                  toast.success('Client created successfully!');
                  setShowAIIntake(false);
                  fetchClients();
                  // Navigate to the new client
                  navigate(`/clients/${response.data.id}`);
                }
              } catch (err) {
                console.error('Failed to create client:', err);
                toast.error('Failed to create client. Please try again.');
              }
            }}
          />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-100">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">On Hold</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.onHold}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, city, or zip code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="client-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading clients...
          </div>
        ) : filteredClients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {search ? "No clients match your search" : "No clients yet"}
              </p>
              {!search && (
                <Link to="/assessments">
                  <Button variant="outline" className="mt-4">
                    Start a New Assessment
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredClients.map((client) => (
              <Card 
                key={client.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/clients/${client.id}`)}
                data-testid={`client-card-${client.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Avatar */}
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
                        style={{ backgroundColor: "hsl(var(--primary))" }}
                      >
                        {(client.first_name || "?").charAt(0).toUpperCase()}
                        {(client.last_name || "").charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate">
                            {client.first_name} {client.last_name}
                          </h3>
                          <Badge 
                            variant="outline" 
                            className={STATUS_STYLES[client.status] || STATUS_STYLES.active}
                          >
                            {STATUS_LABELS[client.status] || client.status}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          {client.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {client.city}, {client.state}
                            </span>
                          )}
                          {client.primary_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {client.primary_phone}
                            </span>
                          )}
                          {client.service_start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Since {new Date(client.service_start_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Chevron */}
                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
